import React, { useCallback, useRef, memo, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import { useTypedSelector } from '@hooks';
import { useCurrentWorkspace } from '@hooks/workspace';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import DropdownAssetVersions from '@components/MediaViewerHeader/components/DropdownAssetVersions';

import './HeaderBreadcrumbs.less';

type HeaderApprovalBreadcrumbsProps = {
  approvalWorkflowId?: string;
  password?: string | null;
};

export default memo(function HeaderApprovalBreadcrumbs({
  approvalWorkflowId,
  password
}: HeaderApprovalBreadcrumbsProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const assetApprovalWorkflowId = assets?.asset?.approvalWorkflow?.id;

  const { data } = useGetApprovalWorkflow({
    enabled: !!assetApprovalWorkflowId,
    params: {
      id: approvalWorkflowId || assetApprovalWorkflowId || '',
      password
    }
  });

  const locationStateRef = useRef<{ returnTo: string } | undefined>(undefined);

  const [currentWorkspace] = useCurrentWorkspace(false);
  const approvalWorkspace =
    currentWorkspace?.id === data?.approvalWorkflow.workspaceId
      ? currentWorkspace
      : undefined;

  useEffect(() => {
    if (!state) return;
    locationStateRef.current = state;
  }, [state]);

  const goBack = useCallback(() => {
    const returnTo = locationStateRef.current?.returnTo;
    if (returnTo) {
      locationStateRef.current = undefined;
      navigate(returnTo);
      return;
    }
    navigate(-1);
  }, [navigate]);
  return (
    <div className="media_viewer_breadcrumbs">
      <div className="media_viewer_breadcrumbs-wr">
        <Button className="media_viewer_btn b-only-icon" onClick={goBack}>
          <ArrowLeftSvg />
        </Button>
        <div className="media_viewer_breadcrumbs-navigation">
          {!!approvalWorkspace && (
            <div
              className="media_viewer_breadcrumbs-item"
              onClick={() => navigate(`/approvals/${approvalWorkspace.id}`)}
            >
              <span className="item-name">Approvals</span>
            </div>
          )}
          {!!approvalWorkspace && (
            <div
              className="media_viewer_breadcrumbs-item"
              onClick={() =>
                navigate(`/approvals/${approvalWorkspace.id}?tab=review`)
              }
            >
              <span className="item-name">Review</span>
            </div>
          )}
          {data?.approvalWorkflow.name && (
            <div
              className="media_viewer_breadcrumbs-item"
              onClick={() =>
                navigate(
                  `/approval?id=${data.approvalWorkflow.id}&forceBack=true`
                )
              }
            >
              <Tooltip
                title={data.approvalWorkflow.name}
                overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                overlayInnerStyle={{ position: 'relative', top: '-30px' }}
              >
                <span className="item-name">{data.approvalWorkflow.name}</span>
              </Tooltip>
            </div>
          )}
          {!!selectedVersion?.name && (
            <div className="media_viewer_breadcrumbs-item">
              <Tooltip
                title={`${selectedVersion.name}.${selectedVersion.extension}`}
                overlayClassName="toggle_favorite_overlay  toggle_favorite_overlay--small"
                overlayInnerStyle={{ position: 'relative', top: '-30px' }}
              >
                <span className="item-name">
                  {selectedVersion.name}.{selectedVersion.extension}
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {!!selectedVersion && <DropdownAssetVersions external={true} />}
        {!!assets?.asset?.permissions.uploadNewVersion && (
          <Button
            className="media_viewer_btn b-only-icon new-version-btn"
            onClick={() => {
              dispatch(
                toggleUploadAssetModal({
                  visible: true,
                  newVersionFromMediaViewer: true,
                  workspaceId: assets.workspaceId
                })
              );
            }}
          >
            <PlusSvg className="b-white-icon" />
          </Button>
        )}
      </div>
    </div>
  );
});
